/**
 * ModalWrapper - Reusable modal container component
 * Extracted from App.js inline modals to reduce duplication
 */
import React from 'react';
import { X } from 'lucide-react';

/**
 * Standard modal wrapper with backdrop and close button
 * @param {boolean} open - Whether modal is visible
 * @param {function} onClose - Close handler
 * @param {React.ReactNode} children - Modal content
 * @param {string} maxWidth - Tailwind max-width class (default: 'max-w-2xl')
 * @param {string} title - Optional title for the modal
 * @param {boolean} closeOnBackdrop - Whether clicking backdrop closes modal (default: true)
 * @param {number} zIndex - z-index level (default: 50)
 * @param {string} className - Additional classes for the modal container
 */
export const ModalWrapper = ({ 
  open, 
  onClose, 
  children, 
  maxWidth = 'max-w-2xl',
  title,
  closeOnBackdrop = true,
  zIndex = 50,
  className = ''
}) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-[${zIndex}] p-4`}
      onClick={handleBackdropClick}
    >
      <div 
        className={`bg-white rounded-lg ${maxWidth} w-full max-h-[90vh] overflow-y-auto shadow-xl ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}
        <div className={title ? 'p-6' : ''}>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Fullscreen modal variant for larger content
 */
export const FullscreenModal = ({ 
  open, 
  onClose, 
  children,
  title,
  zIndex = 50
}) => {
  if (!open) return null;

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-start justify-center z-[${zIndex}] overflow-y-auto`}>
      <div className="w-full max-w-6xl my-8 mx-4">
        <div className="bg-white rounded-lg shadow-xl">
          {title && (
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold">{title}</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default ModalWrapper;
